import React, { useCallback, useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLocale } from '../../contexts/LocaleContext.jsx';
import { useAccountFxPreferences } from '../../contexts/AccountFxPreferences.jsx';
import { useBroadcastGuard } from '../../contexts/BroadcastSurface.jsx';
import '../../styles/partial/PhotosensitivityWarning.css';

/** Where "Go to Photosensitivity mode" leads: the profile's toggle (ENG-836). */
export const PHOTOSENSITIVITY_SETTING_PATH = '/profile#photosensitivity';

const FOCUSABLE = 'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * The first-login photosensitivity warning (ENG-836), mounted once at app
 * level. It opens for a signed-in listener whose account has never
 * acknowledged it (`photosensitivityWarningAckAt: null`), on whatever page
 * they land on, and never while the account is still loading or failed to
 * load. While it is open stream effects run reduced (`useReduceEffects`).
 *
 * Two actions only: "Go to Photosensitivity mode" (records the ack and goes
 * to the profile's toggle, which takes focus there) and "Got it". Escape and
 * × count as "Got it". Every way out records the ack once
 * (`POST /me/photosensitivity-warning/ack`), so it never shows again on any
 * device; a failed ack keeps it closed for this browser session.
 *
 * A modal dialog: labelled by its title and described by its body, so both
 * are announced on open; focus moves to the primary action, Tab and
 * Shift+Tab stay inside, and closing hands focus back to whatever had it.
 *
 * Nothing displays over a broadcast (2026-10-27), so where it shows depends on
 * the page (contexts/BroadcastSurface.jsx):
 *
 * - `/obs/...` (the OBS overlay): never.
 * - A page with a safe slot (the stream page's side column): inline there,
 *   beside or below the player and never over it. It is then a non-modal
 *   dialog: still labelled and described, focus still moves to the primary
 *   action (without scrolling the page) and comes back on close, and Escape
 *   inside it still means "Got it", but there is no backdrop and no focus
 *   trap, so the stream stays watchable and usable.
 * - Any other page showing a broadcast (a video player, a stream page still
 *   loading): deferred; it opens on the next page without one, or as soon as
 *   the slot mounts.
 * - Anywhere else: the modal.
 *
 * Effects run reduced for as long as it is unacknowledged, wherever (and
 * whether) it is showing, because `warningOpen` stays true until the ack.
 */
const PhotosensitivityWarning = () => {
  const { t } = useLocale();
  const { warningOpen, acknowledgeWarning } = useAccountFxPreferences();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const panelRef = useRef(null);
  const primaryRef = useRef(null);
  const openerRef = useRef(null);
  // Set when leaving for the profile toggle, which takes focus instead of the opener.
  const leavingRef = useRef(false);
  const uid = useId().replace(/:/g, '');
  const titleId = `photosensitivity-warning-title-${uid}`;
  const bodyId = `photosensitivity-warning-body-${uid}`;

  const { surfaces, slot } = useBroadcastGuard();
  const onObs = pathname.startsWith('/obs/');
  // 'modal' | 'inline' | null (closed, or deferred off a broadcast page).
  const mode = !warningOpen || onObs ? null : slot ? 'inline' : surfaces > 0 ? null : 'modal';
  const open = mode !== null;
  const inline = mode === 'inline';

  const dismiss = useCallback(() => {
    acknowledgeWarning();
  }, [acknowledgeWarning]);

  const goToSetting = useCallback(() => {
    leavingRef.current = true;
    acknowledgeWarning();
    navigate(PHOTOSENSITIVITY_SETTING_PATH);
  }, [acknowledgeWarning, navigate]);

  const dismissRef = useRef(dismiss);
  dismissRef.current = dismiss;

  const restoreFocus = useCallback(() => {
    const opener = openerRef.current;
    openerRef.current = null;
    if (leavingRef.current) return;
    if (opener && opener !== document.body && typeof opener.focus === 'function' && document.contains(opener)) {
      opener.focus();
    }
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    openerRef.current = document.activeElement;
    leavingRef.current = false;
    primaryRef.current?.focus(inline ? { preventScroll: true } : undefined);
    // Non-modal inline: no trap, and Escape is handled on the panel itself.
    if (inline) return restoreFocus;

    // On window, in the capture phase, so it runs before (and stops) the
    // page's own Escape handling, as TipModal does.
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        event.preventDefault();
        dismissRef.current();
        return;
      }
      if (event.key !== 'Tab' || !panelRef.current) return;
      const focusable = Array.from(panelRef.current.querySelectorAll(FOCUSABLE));
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      if (!panelRef.current.contains(active)) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
      } else if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };
    // Focus that leaves the dialog any other way (a click on the page behind
    // it) is brought back.
    const onFocusIn = (event) => {
      if (panelRef.current && !panelRef.current.contains(event.target)) primaryRef.current?.focus();
    };
    window.addEventListener('keydown', onKeyDown, true);
    document.addEventListener('focusin', onFocusIn);
    return () => {
      window.removeEventListener('keydown', onKeyDown, true);
      document.removeEventListener('focusin', onFocusIn);
      restoreFocus();
    };
  }, [open, inline, restoreFocus]);

  // Inline only: Escape while focus is inside the panel. Stopped here so the
  // stream page's own Escape handling does not also run.
  const onPanelKeyDown = useCallback((event) => {
    if (!inline || event.key !== 'Escape') return;
    event.stopPropagation();
    event.preventDefault();
    dismissRef.current();
  }, [inline]);

  if (!open) return null;

  const panel = (
      <div
        ref={panelRef}
        className={`photosensitivity-warning${inline ? ' photosensitivity-warning--inline' : ''}`}
        role="dialog"
        aria-modal={inline ? 'false' : 'true'}
        aria-labelledby={titleId}
        aria-describedby={bodyId}
        onKeyDown={onPanelKeyDown}
        data-testid="photosensitivity-warning"
        data-placement={mode}
      >
        <button
          type="button"
          className="photosensitivity-warning-close"
          aria-label={t('photosensitivity.warning.closeLabel')}
          onClick={dismiss}
          data-testid="photosensitivity-warning-close"
        >
          <span aria-hidden="true">×</span>
        </button>
        <h2 id={titleId} className="photosensitivity-warning-title">{t('photosensitivity.warning.title')}</h2>
        <p id={bodyId} className="photosensitivity-warning-body">{t('photosensitivity.warning.body')}</p>
        <div className="photosensitivity-warning-actions">
          <button
            type="button"
            ref={primaryRef}
            className="photosensitivity-warning-action photosensitivity-warning-action--primary"
            onClick={goToSetting}
            data-testid="photosensitivity-warning-go"
          >
            {t('photosensitivity.warning.goToSetting')}
          </button>
          <button
            type="button"
            className="photosensitivity-warning-action"
            onClick={dismiss}
            data-testid="photosensitivity-warning-got-it"
          >
            {t('photosensitivity.warning.gotIt')}
          </button>
        </div>
      </div>
  );

  if (inline) return createPortal(panel, slot);

  return createPortal(
    <div className="photosensitivity-warning-backdrop" data-testid="photosensitivity-warning-backdrop">
      {panel}
    </div>,
    document.body
  );
};

export default PhotosensitivityWarning;
